import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getEvents } from '../api/endpoints';
import type { User } from '../types';

// ── Design tokens (fleet globals.css) ────────────────────────────────────────
const DARK = '#0A2E1F';
const BRAND = '#00955F';
const ACCENT = '#0066CC';
const DANGER = '#D32020';

// ── LogoMark (satellite icon from fleet brand/logo.tsx, dark variant) ────────
function LogoMark({ size = 44 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="sb-body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F0FDF4" />
          <stop offset="50%" stopColor="#D1FAE5" />
          <stop offset="100%" stopColor="#A7F3D0" />
        </linearGradient>
        <linearGradient id="sb-panel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34D399" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
        <linearGradient id="sb-ring" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00A36C" stopOpacity="0" />
          <stop offset="50%" stopColor="#00A36C" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#58D8E8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse cx="32" cy="32" rx="27" ry="10.5" stroke="url(#sb-ring)" strokeWidth="1.4" transform="rotate(-20 32 32)" />
      <g transform="rotate(20 32 32)">
        <rect x="5"  y="27" width="15" height="10" rx="1.5" fill="url(#sb-panel)" />
        <line x1="9"    y1="27" x2="9"    y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="12.5" y1="27" x2="12.5" y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="16"   y1="27" x2="16"   y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <rect x="44" y="27" width="15" height="10" rx="1.5" fill="url(#sb-panel)" />
        <line x1="48"   y1="27" x2="48"   y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="51.5" y1="27" x2="51.5" y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="55"   y1="27" x2="55"   y2="37" stroke="#58D8E8" strokeOpacity="0.4" strokeWidth="0.6" />
        <line x1="20" y1="32" x2="24" y2="32" stroke="#A7F3D0" strokeWidth="1.6" />
        <line x1="40" y1="32" x2="44" y2="32" stroke="#A7F3D0" strokeWidth="1.6" />
        <rect x="23" y="25" width="18" height="14" rx="3" fill="url(#sb-body)" stroke="#64748B" strokeWidth="0.4" />
        <rect x="25" y="27" width="14" height="10" rx="1.5" fill="#064E3B" fillOpacity="0.22" />
        <circle cx="32" cy="32" r="2.6" fill="#FFFFFF" />
        <circle cx="32" cy="32" r="1.2" fill="#00A36C" />
      </g>
      <g>
        <path d="M 44 16 Q 49 19 51 24" stroke="#00A36C" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.9" />
        <path d="M 47 12 Q 54 16 56 24" stroke="#00A36C" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.55" />
      </g>
    </svg>
  );
}

// ── Inline icon paths (lucide equivalents) ────────────────────────────────────
const ICONS = {
  map:     'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4',
  frota:   'M1 3h15v13H1z M16 8h4l3 3v5h-7V8z M5 19a2 2 0 100-4 2 2 0 000 4z M16 19a2 2 0 100-4 2 2 0 000 4z',
  history: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  alerts:  'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0',
  admin:   'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  logout:  'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
  menu:    'M4 6h16M4 12h16M4 18h16',
};

function Icon({ d, size = 18, strokeWidth = 2 }: { d: string; size?: number; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

// ── User avatar (avatar photo → gradient initials) ────────────────────────────
function UserAvatar({ user, size = 36 }: { user: User | null; size?: number }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = user?.email.charAt(0).toUpperCase() ?? '?';
  const px = `${size}px`;

  if (user?.avatarUrl && !imgFailed) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.email}
        onError={() => setImgFailed(true)}
        style={{ width: px, height: px, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: px,
        height: px,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${BRAND}, ${ACCENT})`,
        fontSize: `${Math.round(size * 0.42)}px`,
        fontWeight: 700,
        color: 'white',
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
  );
}

const NAV_SECTIONS = [
  {
    label: 'Operação',
    items: [
      { to: '/map',     label: 'Mapa ao Vivo', icon: 'map',     badgeKey: null as 'alerts' | null },
      { to: '/frota',   label: 'Frota',         icon: 'frota',   badgeKey: null },
      { to: '/history', label: 'Trajetos',      icon: 'history', badgeKey: null },
      { to: '/alerts',  label: 'Alertas',       icon: 'alerts',  badgeKey: 'alerts' as const },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/admin', label: 'Administração', icon: 'admin', badgeKey: null },
    ],
  },
];

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { data: alertCount = 0 } = useQuery({
    queryKey: ['events', 'unacked'],
    queryFn: () => getEvents({ to: new Date().toISOString() }),
    refetchInterval: 30_000,
    select: (data) => data.data.filter((e) => !e.ackedAt).length,
  });

  const badges: Record<string, number> = { alerts: alertCount };

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="relative flex flex-col flex-1 min-h-0">
      {/* Logo — stacked layout (fleet logo.tsx: LogoMark | divider | wordmark) */}
      <div className="flex justify-center py-6 px-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(44 * 0.28) }}>
          <LogoMark size={44} />
          <span style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.20)', margin: `${Math.round(44 * 0.12)}px 0` }} />
          <span style={{ fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif", fontSize: 13, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.95)', lineHeight: 1 }}>
            GPS PRIME AIR
          </span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-5 overflow-y-auto" style={{ padding: '20px 12px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {NAV_SECTIONS.map((sec) => (
          <div key={sec.label}>
            <div style={{ padding: '0 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.40)', marginBottom: 8 }}>
              {sec.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sec.items.map((item) => {
                const badge = item.badgeKey ? (badges[item.badgeKey] ?? 0) : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    style={({ isActive }) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      height: 40,
                      borderRadius: 8,
                      padding: '0 12px',
                      fontSize: 13.5,
                      fontWeight: 600,
                      textDecoration: 'none',
                      position: 'relative',
                      transition: 'background 0.15s, color 0.15s',
                      background: isActive ? BRAND : 'transparent',
                      color: isActive ? 'white' : 'rgba(255,255,255,0.70)',
                      boxShadow: isActive ? '0 4px 12px rgba(0,149,95,0.22), 0 12px 32px rgba(0,149,95,0.18)' : 'none',
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon d={ICONS[item.icon as keyof typeof ICONS]} size={18} strokeWidth={isActive ? 2.4 : 2} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </span>
                        {badge > 0 && (
                          <span style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 99, fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', background: DANGER, color: 'white', boxShadow: '0 0 0 2px rgba(0,0,0,0.18)' }}>
                            {badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.10)', padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 8, transition: 'background 0.15s' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <UserAvatar user={user} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.95)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
              {user?.email ?? '—'}
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.50)', marginTop: 2, lineHeight: 1.2, textTransform: 'capitalize' }}>
              {user?.role ?? ''}
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="Sair"
            style={{ width: 32, height: 32, borderRadius: 6, color: 'rgba(255,255,255,0.50)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'color 0.15s, background 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.50)'; e.currentTarget.style.background = 'none'; }}
          >
            <Icon d={ICONS.logout} size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sidebarDecor: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/60 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`fixed z-30 inset-y-0 left-0 flex flex-col transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: 264, background: DARK, color: 'white', flexShrink: 0, boxShadow: '4px 0 24px rgba(10,46,31,0.18)', overflow: 'hidden' }}
      >
        {/* Noise overlay */}
        <div
          aria-hidden="true"
          style={{ ...sidebarDecor, mixBlendMode: 'overlay', opacity: 0.08, backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='320'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='320' height='320' filter='url(%23n)' opacity='.5'/%3E%3C/svg%3E\")" }}
        />
        {/* Radial glow */}
        <div
          aria-hidden="true"
          style={{ ...sidebarDecor, background: 'radial-gradient(circle at 20% 0%, rgba(0,163,108,0.18) 0%, transparent 55%), radial-gradient(circle at 90% 90%, rgba(88,216,232,0.08) 0%, transparent 50%)' }}
        />
        {/* Grid */}
        <div
          aria-hidden="true"
          style={{ ...sidebarDecor, opacity: 0.6, backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 75%)' }}
        />
        {/* Route SVG decoration */}
        <svg aria-hidden="true" viewBox="0 0 264 700" preserveAspectRatio="none" style={{ ...sidebarDecor, width: '100%', height: '100%', opacity: 0.5 }} fill="none">
          <defs>
            <linearGradient id="sb-route" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#58D8E8" stopOpacity="0" />
              <stop offset="35%"  stopColor="#00A36C" stopOpacity="0.6" />
              <stop offset="70%"  stopColor="#00A36C" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#58D8E8" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="sb-route-2" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor="#58D8E8" stopOpacity="0" />
              <stop offset="50%"  stopColor="#58D8E8" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#58D8E8" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M -20 120 C 60 200, 220 180, 200 320 C 180 440, 30 460, 80 600 C 110 680, 240 720, 290 780" stroke="url(#sb-route)" strokeWidth="1" strokeDasharray="3 5" strokeLinecap="round" />
          <path d="M 280 260 C 200 290, 60 310, -10 380" stroke="url(#sb-route-2)" strokeWidth="0.8" strokeDasharray="2 6" strokeLinecap="round" opacity="0.55" />
          <path d="M -10 540 C 60 510, 200 530, 280 570" stroke="url(#sb-route-2)" strokeWidth="0.8" strokeDasharray="2 6" strokeLinecap="round" opacity="0.4" />
          <g stroke="#FFFFFF" strokeWidth="0.4" opacity="0.07">
            <path d="M -10 220 Q 132 240, 274 220" fill="none" />
            <path d="M -10 410 Q 132 430, 274 410" fill="none" />
          </g>
          <g fill="#FFFFFF">
            <circle cx="200" cy="60"  r="0.9" opacity="0.4" />
            <circle cx="20"  cy="280" r="0.8" opacity="0.3" />
            <circle cx="240" cy="380" r="1"   opacity="0.45" />
            <circle cx="20"  cy="600" r="0.7" opacity="0.3" />
            <circle cx="180" cy="660" r="0.8" opacity="0.35" />
            <circle cx="40"  cy="100" r="0.6" opacity="0.25" />
            <circle cx="120" cy="50"  r="0.7" opacity="0.3" />
            <circle cx="260" cy="160" r="0.7" opacity="0.3" />
            <circle cx="250" cy="500" r="0.9" opacity="0.4" />
          </g>
        </svg>
        {/* Orbit decoration bottom-right */}
        <svg aria-hidden="true" viewBox="0 0 264 300" style={{ position: 'absolute', right: -64, bottom: -40, pointerEvents: 'none', opacity: 0.40 }} width="300" height="300" fill="none">
          <defs>
            <linearGradient id="sb-orbit" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#58D8E8" stopOpacity="0.4" />
              <stop offset="50%"  stopColor="#00A36C" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#58D8E8" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <g stroke="url(#sb-orbit)" fill="none" strokeWidth="0.8" transform="rotate(-18 132 150)">
            <ellipse cx="132" cy="150" rx="140" ry="55" strokeOpacity="0.35" />
            <ellipse cx="132" cy="150" rx="110" ry="40" strokeOpacity="0.22" />
            <ellipse cx="132" cy="150" rx="80"  ry="28" strokeOpacity="0.15" />
          </g>
        </svg>

        <SidebarContent onClose={() => setSidebarOpen(false)} />
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Mobile topbar */}
        <header
          className="lg:hidden flex items-center gap-3 px-4 shrink-0"
          style={{ height: 52, background: DARK, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <button onClick={() => setSidebarOpen(true)} style={{ color: 'rgba(255,255,255,0.7)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
            <Icon d={ICONS.menu} size={20} strokeWidth={1.75} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LogoMark size={28} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'white' }}>GPS PRIME AIR</span>
          </div>
        </header>

        <main className="flex-1 flex flex-col overflow-hidden relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
